import {
  listOfflineQueuedOperations,
  markOfflineOperationConflict,
  markOfflineOperationFailed,
  markOfflineOperationSynced,
  markOfflineOperationSyncing,
  type OfflineQueueRecord,
} from './offline-store';
import { queryClient, trpcClient } from './trpc';

const MAX_RETRY_COUNT = 5;

class NavioSystemError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NavioSystemError';
  }
}

export interface OfflineSyncSummary {
  attempted: number;
  synced: number;
  failed: number;
  conflicts: number;
  skipped: number;
  alreadyRunning: boolean;
  offline: boolean;
}

export type OfflineMutationExecutor = (
  procedure: string,
  variables: unknown,
  operation: OfflineQueueRecord
) => Promise<unknown>;

interface OfflineMutationPayload {
  procedure?: unknown;
  variables?: unknown;
  offlineWriteMode?: unknown;
  reviewApprovedAt?: unknown;
}

interface SyncQueueOptions {
  isOnline?: boolean;
  executeMutation?: OfflineMutationExecutor;
  invalidateAfterSync?: boolean;
}

let syncInFlight: Promise<OfflineSyncSummary> | null = null;

function emptySummary(overrides: Partial<OfflineSyncSummary> = {}): OfflineSyncSummary {
  return {
    attempted: 0,
    synced: 0,
    failed: 0,
    conflicts: 0,
    skipped: 0,
    alreadyRunning: false,
    offline: false,
    ...overrides,
  };
}

function browserOnline(): boolean {
  if (typeof navigator === 'undefined') return true;
  return navigator.onLine;
}

function messageFromError(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return 'Offline sync failed';
}

function isConflictError(error: unknown): boolean {
  const message = messageFromError(error).toLowerCase();
  const data = error && typeof error === 'object' ? (error as { data?: unknown }).data : null;
  const dataRecord = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};

  return (
    dataRecord.code === 'CONFLICT' ||
    dataRecord.httpStatus === 409 ||
    message.includes('conflict') ||
    message.includes('already exists')
  );
}

function payloadFromOperation(operation: OfflineQueueRecord): OfflineMutationPayload {
  return operation.payload as OfflineMutationPayload;
}

function needsManualReview(operation: OfflineQueueRecord): boolean {
  const payload = payloadFromOperation(operation);
  if (
    payload.offlineWriteMode === 'queue_review_approved' &&
    typeof payload.reviewApprovedAt === 'string'
  ) {
    return false;
  }

  return (
    payload.offlineWriteMode === 'queue_with_review' ||
    operation.conflictPolicy === 'human_review_required' ||
    operation.conflictPolicy === 'server_authoritative'
  );
}

function retryDelayActive(operation: OfflineQueueRecord, now = Date.now()): boolean {
  if (!operation.nextRetryAt) return false;
  const retryAt = new Date(operation.nextRetryAt).getTime();
  return Number.isFinite(retryAt) && retryAt > now;
}

async function defaultExecuteMutation(
  procedure: string,
  variables: unknown,
  operation: OfflineQueueRecord
): Promise<unknown> {
  const client = trpcClient as {
    mutation?: (
      path: string,
      input: unknown,
      options?: { context?: Record<string, unknown> }
    ) => Promise<unknown>;
  };
  if (!client.mutation) {
    throw new NavioSystemError('tRPC client does not expose mutation replay');
  }
  return client.mutation(procedure, variables, {
    context: { idempotencyKey: operation.idempotencyKey },
  });
}

async function processOfflineOperation(
  operation: OfflineQueueRecord,
  executeMutation: OfflineMutationExecutor
): Promise<Pick<OfflineSyncSummary, 'attempted' | 'synced' | 'failed' | 'conflicts' | 'skipped'>> {
  if (operation.status === 'failed' && retryDelayActive(operation)) {
    return { attempted: 0, synced: 0, failed: 0, conflicts: 0, skipped: 1 };
  }

  if (operation.retryCount >= MAX_RETRY_COUNT) {
    await markOfflineOperationConflict(operation.id, 'Retry limit reached; manual review required');
    return { attempted: 0, synced: 0, failed: 0, conflicts: 1, skipped: 1 };
  }

  if (needsManualReview(operation)) {
    await markOfflineOperationConflict(operation.id, 'Manual review required before cloud sync');
    return { attempted: 0, synced: 0, failed: 0, conflicts: 1, skipped: 1 };
  }

  const payload = payloadFromOperation(operation);
  if (typeof payload.procedure !== 'string') {
    await markOfflineOperationFailed(operation.id, 'Queued operation is missing procedure metadata');
    return { attempted: 0, synced: 0, failed: 1, conflicts: 0, skipped: 0 };
  }

  await markOfflineOperationSyncing(operation.id);
  try {
    await executeMutation(payload.procedure, payload.variables, operation);
    await markOfflineOperationSynced(operation.id);
    return { attempted: 1, synced: 1, failed: 0, conflicts: 0, skipped: 0 };
  } catch (error) {
    if (isConflictError(error)) {
      await markOfflineOperationConflict(operation.id, messageFromError(error));
      return { attempted: 1, synced: 0, failed: 0, conflicts: 1, skipped: 0 };
    }
    await markOfflineOperationFailed(operation.id, messageFromError(error));
    return { attempted: 1, synced: 0, failed: 1, conflicts: 0, skipped: 0 };
  }
}

async function runOfflineQueueSync({
  isOnline = browserOnline(),
  executeMutation = defaultExecuteMutation,
  invalidateAfterSync = true,
}: SyncQueueOptions = {}): Promise<OfflineSyncSummary> {
  if (!isOnline) return emptySummary({ offline: true });

  const summary = emptySummary();
  const operations = await listOfflineQueuedOperations(['queued', 'failed']);

  for (const operation of operations) {
    const result = await processOfflineOperation(operation, executeMutation);
    summary.attempted += result.attempted;
    summary.synced += result.synced;
    summary.failed += result.failed;
    summary.conflicts += result.conflicts;
    summary.skipped += result.skipped;
  }

  if (summary.synced > 0 && invalidateAfterSync) {
    await queryClient.invalidateQueries();
  }

  return summary;
}

export function syncNavioBuildOfflineQueue(
  options: SyncQueueOptions = {}
): Promise<OfflineSyncSummary> {
  if (syncInFlight) return Promise.resolve(emptySummary({ alreadyRunning: true }));

  syncInFlight = runOfflineQueueSync(options).finally(() => {
    syncInFlight = null;
  });

  return syncInFlight;
}

export function resetOfflineSyncForTests(): void {
  syncInFlight = null;
}
